import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import T from './styles/theme';
import { Avatar, fmtDollar } from './components/UI';
import Icon from './components/Icon';
import { StoreProvider, useStore } from './data/store';
import { AuthProvider, useAuth, ROLES } from './data/auth';
import { initDevice, onCallEvent, acceptIncoming, rejectIncoming, getStatus } from './data/twilio';
import Dashboard from './pages/Dashboard';
import ContactsList from './pages/ContactsList';
import ContactRecord from './pages/ContactRecord';
import DealsPage from './pages/DealsPage';
import DialerPage from './pages/DialerPage';
import CallLogsPage from './pages/CallLogsPage';
import TasksPage from './pages/TasksPage';
import ProjectsPage from './pages/ProjectsPage';
import CalendarPage from './pages/CalendarPage';
import AnalyticsPage from './pages/AnalyticsPage';
import EmailPage from './pages/EmailPage';
import SMSPage from './pages/SMSPage';
import WorkflowsPage from './pages/WorkflowsPage';
import DealRecord from './pages/DealRecord';
import CompaniesPage from './pages/CompaniesPage';
import CompanyRecord from './pages/CompanyRecord';
import ListsPage from './pages/ListsPage';
import AdminCenter from './pages/AdminCenter';
import CustomPipelinePage from './pages/CustomPipelinePage';
import AIAgentsPage from './pages/AIAgentsPage';
import SheetsPage from './pages/SheetsPage';
import TeamChatPage from './pages/TeamChatPage';
import VoiceCommand from './components/VoiceCommand';
import ActiveCallBar from './components/ActiveCallBar';
import NotificationBell from './components/NotificationBell';
import PhoneWidget from './components/PhoneWidget';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import BillingRequiredPage from './components/BillingRequiredPage';
import SetupChecklistPage, { isSetupComplete, getSetupProgress } from './pages/SetupChecklistPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BookingPage from './pages/BookingPage';
import UserProfile from './pages/UserProfile';
import LinkedInPage from './pages/LinkedInPage';
import OutboundAgentPage from './pages/OutboundAgentPage';

// ── Pipeline Doctor header badge — shows count of at-risk deals ──
function PipelineDoctorBadge({ onClick }) {
  const [count, setCount] = React.useState(0);
  const auth = useAuth();
  const orgId = auth?.currentUser?._orgId || null;

  React.useEffect(() => {
    if (!orgId) return;
    let mounted = true;
    const supabaseImport = require('./data/supabase').default;

    async function load() {
      const { data } = await supabaseImport.from("pipeline_alerts").select("id", { count: "exact" }).eq("org_id", orgId).eq("status", "active");
      if (mounted) setCount(data?.length || 0);
    }
    load();

    const channel = supabaseImport.channel("doc-badge-" + orgId)
      .on("postgres_changes", { event: "*", schema: "public", table: "pipeline_alerts", filter: `org_id=eq.${orgId}` }, () => { load(); })
      .subscribe();

    // Same-tab sync fallback (fires from AIAgentsPage dismiss/sendAction).
    // Handles the case where realtime drops events or isn't enabled on the table.
    const onAlertsChanged = () => load();
    window.addEventListener("coreflow_pipeline_alerts_changed", onAlertsChanged);

    // Cross-tab / backgrounded-tab reconciliation — a cron run that adds or
    // resolves alerts while the tab is hidden gets picked up on refocus.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);

    return () => {
      mounted = false;
      supabaseImport.removeChannel(channel);
      window.removeEventListener("coreflow_pipeline_alerts_changed", onAlertsChanged);
      window.removeEventListener("focus", onFocus);
    };
  }, [orgId]);

  if (count === 0) return null;
  return (
    <button onClick={onClick} title={`${count} deal${count !== 1 ? "s" : ""} need attention`} style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(229,57,53,0.3)", background: "rgba(229,57,53,0.08)", color: "#E53935", fontSize: 10, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
      <Icon name="target" size={12} color="#E53935" />
      {count}
    </button>
  );
}

// ── Coach sidebar badge — opens coaching for most recent call ──
function CoachBadge() {
  const [latestCoaching, setLatestCoaching] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(false);
  const orgId = window.__coreflow_org_id;

  React.useEffect(() => {
    if (!orgId) return;
    const supabaseImport = require('./data/supabase').default;
    supabaseImport.from("call_coaching")
      .select("*, call_analysis_id")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (data) setLatestCoaching(data); });
  }, [orgId]);

  if (!latestCoaching) return null;

  const CoachingPopup = require('./components/CoachingPopup').default;
  return (<>
    <button onClick={() => setShowPopup(true)} title="Call coaching" style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(107,131,212,0.3)", background: "rgba(107,131,212,0.08)", color: "#6B83D4", fontSize: 11, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
      <Icon name="target" size={12} color="#6B83D4" /> <span style={{ fontSize: 10 }}>{latestCoaching.overall_score}/10</span>
    </button>
    {showPopup && <CoachingPopup analysisId={latestCoaching.call_analysis_id} contactName="" date={latestCoaching.created_at} onClose={() => setShowPopup(false)} />}
  </>);
}

function AppInner() {
  const store = useStore();
  const auth = useAuth();

  // ── URL-based page persistence ──
  const parseHash = () => {
    const raw = window.location.pathname.slice(1); // remove leading /
    // Strip query params
    const clean = raw.split("?")[0];
    if (!clean) return { page: "dashboard", contactId: null, dealId: null, companyId: null, subTab: null };
    const parts = clean.split("/");
    let page = parts[0] || "dashboard";
    // Redirect invite URLs to dashboard (invite already processed by auth)
    if (page === "invite") {
      window.history.replaceState({}, "", "/dashboard");
      return { page: "dashboard", contactId: null, dealId: null, companyId: null, subTab: null };
    }
    if (page === "contact-record" && parts[1]) return { page, contactId: parts[1], dealId: null, companyId: null, subTab: null };
    if (page === "deal-record" && parts[1]) return { page, contactId: null, dealId: parts[1], companyId: null, subTab: null };
    if (page === "company-record" && parts[1]) return { page, contactId: null, dealId: null, companyId: parts[1], subTab: null };
    // Admin sub-routes: #admin/phone-numbers → page="admin", subTab="phone_numbers"
    const subTab = parts[1] ? parts[1].replace(/-/g, "_") : null;
    return { page, contactId: null, dealId: null, companyId: null, subTab };
  };

  const initial = parseHash();
  const [page, setPageState] = useState(initial.page);
  const [selectedContactId, setSelectedContactId] = useState(initial.contactId);
  const [selectedDeal, setSelectedDeal] = useState(initial.dealId);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initial.companyId);
  const [adminSubTab, setAdminSubTab] = useState(initial.subTab);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHovered, setSearchHovered] = useState(null);
  const searchRef = useRef(null);
  const searchDropRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) &&
          (!searchDropRef.current || !searchDropRef.current.contains(e.target))) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Animated page transitions ──
  const pageRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const pendingNav = useRef(null);

  const setPage = useCallback((p) => {
    // If already on this page, skip animation
    if (p === page && !pendingNav.current) {
      return;
    }
    // If already transitioning, queue the navigation
    if (isTransitioning) {
      pendingNav.current = p;
      return;
    }

    // Start exit animation
    setIsTransitioning(true);
    const el = pageRef.current;
    if (el) {
      el.classList.remove("cf-page-enter");
      el.classList.add("cf-page-exit");
    }

    // After exit animation completes, swap page and enter
    setTimeout(() => {
      setPageState(p);
      // Parse sub-tab from page string like "admin/phone-numbers"
      if (p.includes("/")) {
        const [basePage, sub] = p.split("/");
        setPageState(basePage);
        setAdminSubTab(sub.replace(/-/g, "_"));
        window.history.pushState({}, "", "/" + p);
      } else {
        if (p !== "contact-record" && p !== "deal-record") {
          window.history.pushState({}, "", "/" + p);
        }
        if (p !== "admin") setAdminSubTab(null);
      }
      // Small delay to let React render the new content before animating in
      requestAnimationFrame(() => {
        const newEl = pageRef.current;
        if (newEl) {
          newEl.classList.remove("cf-page-exit");
          newEl.classList.add("cf-page-enter");
          // Remove animation class after animations complete so dropdowns render solid
          setTimeout(() => { if (newEl) newEl.classList.remove("cf-page-enter"); }, 600);
        }
        setIsTransitioning(false);

        // Process queued navigation if any
        if (pendingNav.current) {
          const next = pendingNav.current;
          pendingNav.current = null;
          setTimeout(() => setPage(next), 50);
        }
      });
    }, 220); // Exit animation duration
  }, [page, isTransitioning]);

  // Listen for browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      const { page, contactId, dealId, subTab } = parseHash();
      setPageState(page);
      if (contactId) setSelectedContactId(contactId);
      if (dealId) setSelectedDeal(dealId);
      setAdminSubTab(subTab);
    };
    window.addEventListener("popstate", onHashChange);
    return () => window.removeEventListener("popstate", onHashChange);
  }, []);

  // Auth is guaranteed by AuthGate

  // ── Push Notification Registration ──
  useEffect(() => {
    if (!auth.currentUser?.email) return;
    const registerPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const reg = await navigator.serviceWorker.ready;
        
        // Check if already subscribed
        let sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Already subscribed, save to backend (in case device changed)
          const token = (await (await import('./data/supabase')).default.auth.getSession())?.data?.session?.access_token;
          if (token) {
            fetch('/api/push/subscribe', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
              body: JSON.stringify({ subscription: sub.toJSON() }),
            }).catch(() => {});
          }
          return;
        }
        
        // Get VAPID key from server
        const keyRes = await fetch('/api/push/subscribe');
        if (!keyRes.ok) return;
        const { publicKey } = await keyRes.json();
        if (!publicKey) return;
        
        // Ask permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        
        // Subscribe
        const applicationServerKey = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/').replace(/=+$/, '') + '=='.slice(0, (4 - publicKey.length % 4) % 4)), c => c.charCodeAt(0));
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
        
        // Save subscription to backend
        const token = (await (await import('./data/supabase')).default.auth.getSession())?.data?.session?.access_token;
        if (token) {
          await fetch('/api/push/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({ subscription: sub.toJSON() }),
          });
          console.log('[CoreFlow] ✅ Push notifications registered');
        }
      } catch (e) {
        console.warn('[CoreFlow] Push registration:', e.message);
      }
    };
    // Delay push registration to not block initial render
    const timer = setTimeout(registerPush, 3000);
    return () => clearTimeout(timer);
  }, [auth.currentUser?.email]);

  // ── Listen for push navigation messages from service worker ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'PUSH_NAVIGATE') {
        const route = event.data.url?.replace('/', '') || 'dashboard';
        setPage(route);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [setPage]);

  // ── Global Twilio Device: auto-init + incoming calls from any page ──
  const [globalIncoming, setGlobalIncoming] = useState(null);
  const [twilioInitialized, setTwilioInitialized] = useState(false);
  const twilioInitPending = useRef(false);

  // Register call event listeners immediately
  useEffect(() => {
    onCallEvent("incoming", (e) => {
      console.log("[CoreFlow] 📞 Global incoming call from:", e.from);
      setGlobalIncoming({ from: e.from });
    });
    onCallEvent("connected", () => setGlobalIncoming(null));
    onCallEvent("ended", () => setGlobalIncoming(null));
    onCallEvent("rejected", () => setGlobalIncoming(null));
  }, []);

  // Init Twilio after first user interaction (avoids AudioContext browser block)
  useEffect(() => {
    if (!auth.currentUser?.email || twilioInitialized) return;
    twilioInitPending.current = true;

    const doInit = () => {
      if (!twilioInitPending.current || twilioInitialized) return;
      twilioInitPending.current = false;
      const email = auth.currentUser?.email;
      if (!email) return;
      console.log("[CoreFlow] Global Twilio init for:", email);
      initDevice(email).then(ok => {
        if (ok?.success) {
          setTwilioInitialized(true);
          console.log("[CoreFlow] ✅ Global Twilio device ready");
        }
      });
    };

    // Listen for first user interaction
    const events = ["click", "keydown", "touchstart"];
    const handler = () => {
      events.forEach(e => document.removeEventListener(e, handler));
      doInit();
    };
    events.forEach(e => document.addEventListener(e, handler, { once: true }));

    return () => events.forEach(e => document.removeEventListener(e, handler));
  }, [auth.currentUser?.email, twilioInitialized]);

  const handleAcceptGlobal = () => {
    setPage("dialer");
    // Small delay to let DialerPage mount before accepting
    setTimeout(() => {
      acceptIncoming();
      setGlobalIncoming(null);
    }, 100);
  };

  const handleRejectGlobal = () => {
    rejectIncoming();
    setGlobalIncoming(null);
  };

  const handleSelectContact = (contactOrId) => {
    const id = typeof contactOrId === "object" && contactOrId !== null ? contactOrId.id : contactOrId;
    // Trigger exit animation on current page
    const el = pageRef.current;
    if (el) {
      el.classList.remove("cf-page-enter");
      el.classList.add("cf-page-exit");
    }
    setTimeout(() => {
      setSelectedContactId(id);
      setSelectedDeal(null);
      setPageState("contact-record");
      window.history.pushState({}, "", "/contact-record/" + id);
    }, 200);
  };

  const handleSelectDeal = (dealId) => {
    const el = pageRef.current;
    if (el) {
      el.classList.remove("cf-page-enter");
      el.classList.add("cf-page-exit");
    }
    setTimeout(() => {
      setSelectedDeal(dealId);
      setPageState("deal-record");
      window.history.pushState({}, "", "/deal-record/" + dealId);
    }, 200);
  };

  const handleSelectCompany = (companyId) => {
    const el = pageRef.current;
    if (el) {
      el.classList.remove("cf-page-enter");
      el.classList.add("cf-page-exit");
    }
    setTimeout(() => {
      setSelectedCompanyId(companyId);
      setPageState("company-record");
      window.history.pushState({}, "", "/company-record/" + companyId);
    }, 200);
  };

  const handleBack = () => { setSelectedContactId(null); setPage("contacts"); };
  const handleBackDeals = () => { setSelectedDeal(null); setPage("deals"); };
  const handleBackCompanies = () => { setSelectedCompanyId(null); setPage("companies"); };

  const selectedContact = useMemo(
    () => selectedContactId ? store.contacts.find(c => c.id === selectedContactId) : null,
    [selectedContactId, store.contacts]
  );
  const role = ROLES[auth.currentUser?.role] || {};

  const baseNav = [
    { id: "dashboard", label: "Mission Control", icon: "target" },
    { id: "contacts", label: "People", perm: "contacts_view", icon: "user" },
    { id: "lists", label: "Lists", perm: "contacts_view", icon: "list" },
    { id: "companies", label: "Companies", perm: "contacts_view", icon: "building" },
    { id: "email", label: "Email", perm: "email_send", icon: "mail" },
    { id: "sms", label: "SMS", perm: "email_send", icon: "chat" },
    { id: "deals", label: "Pipeline", perm: "deals_view", icon: "briefcase" },
    { id: "dialer", label: "Dialer", perm: "dialer_use", icon: "phoneFilled" },
    { id: "call-logs", label: "Call Logs", perm: "dialer_use", icon: "phone" },
    { id: "tasks", label: "Tasks", perm: "tasks_view", icon: "check" },
    { id: "projects", label: "Projects", icon: "folder" },
    { id: "calendar", label: "Calendar", icon: "calendar" },
    // Temporarily hidden pre-trial — re-enable for full launch.
    // { id: "sheets", label: "Sheets", icon: "grid" },
    { id: "workflows", label: "Automations", perm: "workflows_view", icon: "workflow" },
    { id: "analytics", label: "Intelligence", perm: "analytics_view", icon: "barChart" },
    { id: "team-chat", label: "Team Chat", icon: "chat" },
    // Temporarily hidden pre-trial — re-enable for full launch.
    // { id: "linkedin", label: "LinkedIn", icon: "linkedin" },
    { id: "outbound", label: "The Prospector", icon: "send" },
    { id: "ai-agents", label: "Agents", perm: "ai_agents", icon: "zap" },
  ];

  // Filter nav by permissions + role-based visibility
  const userRole = auth.currentUser?.role || "admin";
  const roleHidden = {
    sdr: ["lists", "workflows", "analytics"],
    viewer: ["lists", "workflows", "analytics", "ai-agents"],
  };
  const hiddenForRole = roleHidden[userRole] || [];
  const filteredNav = baseNav.filter(n => {
    if (hiddenForRole.includes(n.id)) return false;
    if (n.perm && !auth.hasPermission(n.perm)) return false;
    return true;
  });

  // Add custom pipelines to nav
  const customPipeNav = store.customPipelines.map(p => ({
    id: "pipeline_" + p.id, label: p.name, custom: true,
  }));

  const nav = [...filteredNav, ...customPipeNav];
  if (!isSetupComplete()) nav.splice(1, 0, { id: "setup", label: "Setup", icon: "check", setup: true });
  if (auth.hasPermission("admin_settings")) nav.push({ id: "admin", label: "Settings", icon: "settings" });

  const pipelineTitle = page.startsWith("pipeline_") ? (store.customPipelines.find(p => "pipeline_" + p.id === page)?.name || "Pipeline") : null;

  const titles = {
    dashboard: "Mission Control", contacts: "People", lists: "Lists & Segments", companies: "Companies", email: "Email", sms: "SMS",
    deals: "Pipeline", dialer: "Dialer", "call-logs": "Call Logs", tasks: "Tasks", projects: "Projects",
    workflows: "Automations", analytics: "Intelligence", "ai-agents": "Core Agents",
    admin: "Settings", profile: "My Profile", setup: "Setup",
    "contact-record": selectedContact ? selectedContact.fn + " " + selectedContact.ln : "Contact",
    "deal-record": "Deal Record",
    ...(pipelineTitle ? { [page]: pipelineTitle } : {}),
  };

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ── Mobile detection ──
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    // Auto-close sidebar on mobile on mount
    if (window.innerWidth < 768) setSidebarOpen(false);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const Sidebar = ({ full }) => {
    const isActive = (id) => full && page === id;
    const collapsed = !isMobile && full && !sidebarOpen;
    const w = isMobile ? 260 : (collapsed ? 62 : 230);
    const mobileHidden = isMobile && !sidebarOpen;

    if (mobileHidden) return null;

    return (
    <>
    {/* Mobile overlay backdrop */}
    {isMobile && sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
    <div style={{
      width: w, display: "flex", flexDirection: "column", flexShrink: 0,
      background: isMobile ? "#2C2E33" : "transparent",
      borderRight: isMobile ? "1px solid rgba(217,219,221,0.12)" : "none",
      transition: "width 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      overflow: "hidden",
      ...(isMobile ? { position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 999, paddingTop: "env(safe-area-inset-top, 0px)" } : { position: "relative" }),
    }}>
      {/* Logo */}
      <div style={{ padding: collapsed ? "16px 10px" : "16px 20px", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", borderBottom: "1px solid rgba(217,219,221,0.08)", minHeight: 62, position: "relative", overflow: "hidden" }}>
        <img src="/cf-icon-white.png" alt="CF" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0, opacity: collapsed ? 1 : 0, position: collapsed ? "relative" : "absolute", transition: "opacity 0.25s ease", pointerEvents: collapsed ? "auto" : "none" }} />
        <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 22, objectFit: "contain", opacity: collapsed ? 0 : 1, position: collapsed ? "absolute" : "relative", transition: "opacity 0.25s ease", pointerEvents: collapsed ? "none" : "auto" }} />
      </div>
      {/* Nav */}
      <div style={{ flex: 1, padding: collapsed ? "14px 8px" : "14px 12px", overflowY: "auto" }}>
        {nav.map(n => {
          const active = isActive(n.id);
          const isAI = n.id === "ai-agents";
          return (
          <button key={n.id} onClick={() => { setPage(n.id); setSelectedContactId(null); setSelectedDeal(null); if (isMobile) setSidebarOpen(false); }}
            title={collapsed ? n.label : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 11, padding: collapsed ? "9px 0" : "9px 14px", borderRadius: 10,
              border: "none", width: "100%", textAlign: "left",
              justifyContent: collapsed ? "center" : "flex-start",
              background: active ? "rgba(64,89,173,0.12)" : "transparent",
              marginBottom: 2, cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
              borderLeft: active ? "2px solid rgba(250,250,250,0.30)" : "2px solid transparent",
              
              boxShadow: "none",
            }}>
            <Icon name={n.icon || (n.custom ? "grid" : "dot")} size={15} color={active ? T.blue : "#FAFAFA"} />
            {!collapsed && <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? "#FAFAFA" : "#FAFAFA", opacity: active ? 1 : 0.6, letterSpacing: ".01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{n.label}</span>}
            {!collapsed && n.setup && (() => { const p = getSetupProgress(); return <span style={{ fontSize: 9, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 5, flexShrink: 0 }}>{p.pct}%</span>; })()}
          </button>
          );
        })}
      </div>
      {/* Bottom section */}
      <div style={{ padding: collapsed ? "12px 8px" : "12px", borderTop: "1px solid rgba(217,219,221,0.12)" }}>
        <button onClick={() => setPage("profile")} style={{
          display: "flex", alignItems: "center", gap: 9, padding: collapsed ? "9px 0" : "9px 12px", width: "100%", border: "none",
          background: page === "profile" ? "rgba(64,89,173,0.12)" : "transparent",
          borderRadius: 10, cursor: "pointer", textAlign: "left",
          justifyContent: collapsed ? "center" : "flex-start",
          transition: "all 0.2s ease",
        }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden", flexShrink: 0, border: "1.5px solid rgba(255,255,255,0.35)", position: "relative", background: "rgba(64,89,173,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 10, fontWeight: 400, color: "#8FB4FF" }}>{auth.currentUser?.avatar || "?"}</span>
                {auth.currentUser?.avatarUrl && <img src={auth.currentUser.avatarUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
          </div>
          {!collapsed && <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{auth.currentUser?.firstName} {auth.currentUser?.lastName}</div>
            <div style={{ fontSize: 9, color: "#FAFAFA", fontWeight: 400 }}>{role.label || auth.currentUser?.role}</div>
          </div>}
          {!collapsed && <button onClick={(e) => { e.stopPropagation(); auth.logout(); }}
            style={{ background: "none", border: "none", color: "#FAFAFA", cursor: "pointer", padding: 4 }}
            title="Sign out"><Icon name="power" size={13} color="#FAFAFA" /></button>}
        </button>
        {/* Collapse toggle */}
        {full && (
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
            display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start",
            gap: 9, width: "100%", padding: collapsed ? "10px 0" : "10px 14px",
            marginTop: 6, borderRadius: 10, border: "none",
            background: "rgba(217,219,221,0.08)", cursor: "pointer",
            transition: "all 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(64,89,173,0.10)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: sidebarOpen ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.25s ease", flexShrink: 0 }}>
              <path d="M8.5 3L5 7L8.5 11" stroke={T.tx3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {!collapsed && <span style={{ fontSize: 11, color: T.tx3, fontWeight: 400 }}>Collapse</span>}
          </button>
        )}
      </div>
    </div>
    </>
    );
  };

  // contact-record now renders inside the normal app shell below

  return (
    <div style={{ fontFamily: "'proxima-nova', -apple-system, BlinkMacSystemFont, sans-serif", background: "#2C2E33", color: T.txt, height: "100vh", display: "flex", overflow: "hidden", position: "relative", zIndex: 1, paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
      <Sidebar full />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{
          height: 52, display: "flex", alignItems: "center", padding: isMobile ? "0 12px" : "0 22px",
          borderBottom: "none", flexShrink: 0, gap: isMobile ? 8 : 0,
        }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
              width: 36, height: 36, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
              background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h12M2 12h12" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          )}
          <div style={{
            width: isMobile ? "auto" : 160,
            flexShrink: 0,
            fontSize: isMobile ? 14 : 15,
            fontWeight: 400,
            color: "#FAFAFA",
            overflow: isMobile ? undefined : "hidden",
            textOverflow: isMobile ? undefined : "ellipsis",
            whiteSpace: isMobile ? undefined : "nowrap",
          }} title={typeof titles[page] === "string" ? titles[page] : undefined}>{titles[page]}</div>
          {/* Global Search — hide on very small screens */}
          {!isMobile && <div ref={searchRef} style={{ position: "relative", flex: 1, maxWidth: 400, margin: "0 auto", zIndex: 100 }}>
            <input value={globalSearch} onChange={e => { setGlobalSearch(e.target.value); setSearchOpen(true); setSearchHovered(null); }}
              onFocus={() => { if (globalSearch) setSearchOpen(true); }}
              onKeyDown={e => { if (e.key === "Escape") { setSearchOpen(false); e.target.blur(); }}}
              placeholder="Search contacts, deals, settings..."
              style={{ width: "100%", padding: "8px 12px 8px 36px", borderRadius: 10, border: "1px solid rgba(217,219,221,0.15)", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 12, fontWeight: 400, outline: "none", transition: "border-color .15s ease, box-shadow .15s ease", fontFamily: "inherit" }}
              onFocus={e => { e.target.style.borderColor = "#4059AD"; e.target.style.boxShadow = "0 0 0 2px rgba(64,89,173,0.3)"; }}
              onBlur={e => { e.target.style.borderColor = "rgba(217,219,221,0.15)"; e.target.style.boxShadow = "none"; }} />
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="search" size={13} color={T.tx3} /></span>
            {searchOpen && globalSearch.length >= 2 && (() => {
              const q = globalSearch.toLowerCase();
              const cResults = store.contacts.filter(c => ((c.fn||"") + " " + (c.ln||"") + " " + (c.company||c.co||"") + " " + (c.email||"")).toLowerCase().includes(q)).slice(0, 6);
              const dResults = store.deals.filter(d => (d.name||"").toLowerCase().includes(q)).slice(0, 4);
              const companies = [...new Set(store.contacts.map(c => c.company||c.co).filter(Boolean))].filter(c => c.toLowerCase().includes(q)).slice(0, 3);
              const pageIndex = [
                { label: "Dashboard", desc: "Overview & metrics", route: "dashboard", icon: "D", color: T.blue },
                { label: "Mission Control", desc: "AI command center", route: "mission-control", icon: "M", color: T.pur },
                { label: "Contacts", desc: "Manage all contacts", route: "contacts", icon: "C", color: T.blue, keywords: "people leads prospects" },
                { label: "Companies", desc: "Company accounts", route: "companies", icon: "Co", color: T.pur },
                { label: "Deals", desc: "Sales pipeline deals", route: "deals", icon: "$", color: T.grn, keywords: "pipeline opportunities" },
                { label: "Tasks", desc: "To-dos & follow-ups", route: "tasks", icon: "T", color: T.amb },
                { label: "Email", desc: "Send & manage emails", route: "email", icon: "E", color: T.blue, keywords: "inbox compose" },
                { label: "SMS", desc: "Text messaging", route: "sms", icon: "S", color: T.grn, keywords: "text message" },
                { label: "Dialer", desc: "Make & receive calls", route: "dialer", icon: "Ph", color: T.blue, keywords: "phone call ring" },
                { label: "Call Logs", desc: "Call history & recordings", route: "call-logs", icon: "CL", color: T.tx3, keywords: "call history recordings" },
                { label: "Pipeline", desc: "Visual deal pipeline", route: "pipeline", icon: "P", color: T.pur, keywords: "kanban board stages" },
                { label: "Lists", desc: "Contact lists & segments", route: "lists", icon: "L", color: T.amb, keywords: "segments filters" },
                { label: "Calendar", desc: "Schedule & events", route: "calendar", icon: "Ca", color: T.blue, keywords: "schedule meetings events booking" },
                { label: "Projects", desc: "Project management", route: "projects", icon: "Pr", color: T.pur },
                { label: "AI Agents", desc: "AI-powered automation", route: "ai-agents", icon: "AI", color: T.blue, keywords: "artificial intelligence bots" },
                { label: "Workflows", desc: "Automation workflows", route: "workflows", icon: "W", color: T.amb, keywords: "automation rules triggers" },
                { label: "Analytics", desc: "Reports & insights", route: "analytics", icon: "An", color: T.grn, keywords: "reports charts metrics data" },
                { label: "Team Chat", desc: "Internal messaging", route: "team-chat", icon: "TC", color: T.blue, keywords: "chat messaging slack" },
                // Temporarily hidden pre-trial — re-enable for full launch.
                // { label: "LinkedIn", desc: "LinkedIn accounts & outreach", route: "linkedin", icon: "Li", color: T.blue, keywords: "linkedin social selling outreach connections dms sales navigator leads import kpi" },
                { label: "The Prospector", desc: "Autonomous cold outreach", route: "outbound", icon: "Ag", color: T.grn, keywords: "outbound agent prospector cold email automation domains warmup snov leads sourcing" },
                { label: "Setup", desc: "Setup checklist", route: "setup", icon: "Se", color: T.amb, keywords: "onboarding getting started" },
                { label: "Profile & Account", desc: "Your personal settings", route: "admin/profile", icon: "⚙", color: T.tx2, keywords: "settings account name avatar password" },
                { label: "Notifications", desc: "Notification preferences", route: "admin/notifications", icon: "⚙", color: T.tx2, keywords: "settings alerts push email notifications" },
                { label: "Email Signatures", desc: "Manage email signatures", route: "admin/signatures", icon: "⚙", color: T.tx2, keywords: "settings signature sign-off" },
                { label: "Phone Numbers", desc: "Manage phone numbers & caller ID", route: "admin/phone-numbers", icon: "⚙", color: T.tx2, keywords: "settings twilio caller id phone dialer numbers" },
                { label: "Integrations", desc: "Connect email, calendar & more", route: "admin/integrations", icon: "⚙", color: T.tx2, keywords: "settings connect gmail outlook google calendar twilio api" },
                { label: "Users & Permissions", desc: "Manage team members & roles", route: "admin/users", icon: "⚙", color: T.tx2, keywords: "settings team invite members roles admin permissions" },
                { label: "Custom Fields", desc: "Add custom fields to contacts & deals", route: "admin/fields", icon: "⚙", color: T.tx2, keywords: "settings fields properties custom data" },
                { label: "Pipeline Settings", desc: "Configure deal stages", route: "admin/pipeline", icon: "⚙", color: T.tx2, keywords: "settings stages deal pipeline configure" },
                { label: "Quotas", desc: "Team quotas & KPI targets", route: "admin/team", icon: "⚙", color: T.tx2, keywords: "settings kpi targets goals quotas activity" },
                { label: "Lead Scoring", desc: "Configure lead scoring rules", route: "admin/scoring", icon: "⚙", color: T.tx2, keywords: "settings score points rules lead qualification" },
                { label: "Billing & Plan", desc: "Subscription & payment", route: "admin/billing", icon: "⚙", color: T.tx2, keywords: "settings plan subscription payment upgrade pricing" },
                { label: "General", desc: "Company branding & preferences", route: "admin/general", icon: "⚙", color: T.tx2, keywords: "settings logo branding colors timezone company" },
              ];
              const pageResults = pageIndex.filter(p => (p.label + " " + p.desc + " " + (p.keywords || "")).toLowerCase().includes(q)).slice(0, 5);
              const hasResults = cResults.length + dResults.length + companies.length + pageResults.length > 0;

              // Build unified list for hover preview
              const allResults = [
                ...cResults.map(c => ({ type: "contact", id: c.id, data: c })),
                ...dResults.map(d => ({ type: "deal", id: d.id, data: d })),
                ...companies.map(co => ({ type: "company", id: co, data: co })),
                ...pageResults.map(p => ({ type: "page", id: p.route, data: p })),
              ];
              const hovered = searchHovered ? allResults.find(r => r.type + r.id === searchHovered) : null;
              const showPreview = hovered && (hovered.type === "contact" || hovered.type === "deal");

              // Preview panel renderer
              const renderPreview = () => {
                if (!hovered) return null;
                if (hovered.type === "contact") {
                  const c = hovered.data;
                  const contactDeals = store.deals.filter(d => d.ct === c.id);
                  return (
                    <div style={{ padding: "18px 20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: T.blue + "18", border: "1px solid " + T.blue + "25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 400, color: T.blue }}>{(c.fn||"?")[0]}{(c.ln||"?")[0]}</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{c.fn} {c.ln}</div>
                          <div style={{ fontSize: 11, color: T.tx3 }}>{c.title ? c.title + " · " : ""}{c.company || c.co || ""}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                        {[
                          { label: "View", icon: "→", action: () => { handleSelectContact(c.id); setGlobalSearch(""); setSearchOpen(false); }},
                          ...(c.email ? [{ label: "Email", icon: "✉", action: () => { setPage("email"); setGlobalSearch(""); setSearchOpen(false); }}] : []),
                          ...(c.ph ? [{ label: "Call", icon: "☎", action: () => { setPage("dialer"); setGlobalSearch(""); setSearchOpen(false); }}] : []),
                        ].map(btn => (
                          <button key={btn.label} onClick={btn.action} style={{ flex: 1, padding: "6px 0", borderRadius: 6, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 10, fontWeight: 400, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = T.blue + "18"; e.currentTarget.style.borderColor = T.blue + "40"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = T.bdr; }}
                          ><span style={{ fontSize: 11 }}>{btn.icon}</span> {btn.label}</button>
                        ))}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {c.email && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Email</span><span style={{ fontSize: 10, color: T.txt }}>{c.email}</span></div>}
                        {c.ph && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Phone</span><span style={{ fontSize: 10, color: T.txt }}>{c.ph}</span></div>}
                        {(c.company || c.co) && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Company</span><span style={{ fontSize: 10, color: T.txt }}>{c.company || c.co}</span></div>}
                        {c.source && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Source</span><span style={{ fontSize: 10, color: T.txt }}>{c.source}</span></div>}
                        <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Score</span><span style={{ fontSize: 10, fontWeight: 400, color: c.score >= 80 ? T.grn : c.score >= 50 ? T.amb : T.red }}>{c.score}</span></div>
                        {contactDeals.length > 0 && (
                          <div style={{ marginTop: 4, padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid " + T.bdr }}>
                            <div style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>DEALS ({contactDeals.length})</div>
                            {contactDeals.slice(0, 2).map(d => (
                              <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                <span style={{ fontSize: 10, color: T.txt }}>{d.name}</span>
                                <span style={{ fontSize: 10, fontWeight: 400, color: T.grn }}>{fmtDollar(d.val)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                if (hovered.type === "deal") {
                  const d = hovered.data;
                  const contact = store.contacts.find(c => c.id === d.ct);
                  return (
                    <div style={{ padding: "18px 20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: T.grn + "18", border: "1px solid " + T.grn + "25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 400, color: T.grn }}>$</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{d.name}</div>
                          <div style={{ fontSize: 11, color: T.tx3 }}>{d.stage}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                        <button onClick={() => { handleSelectDeal(d.id); setGlobalSearch(""); setSearchOpen(false); }} style={{ flex: 1, padding: "6px 0", borderRadius: 6, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 10, fontWeight: 400, cursor: "pointer", transition: "all 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.background = T.blue + "18"; e.currentTarget.style.borderColor = T.blue + "40"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = T.bdr; }}
                        >View Deal</button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Value</span><span style={{ fontSize: 10, fontWeight: 400, color: T.grn }}>{fmtDollar(d.val)}</span></div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Stage</span><span style={{ fontSize: 10, color: T.txt }}>{d.stage}</span></div>
                        {d.close && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Close date</span><span style={{ fontSize: 10, color: T.txt }}>{d.close}</span></div>}
                        {d.prob != null && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 10, color: T.tx3 }}>Probability</span><span style={{ fontSize: 10, color: T.txt }}>{d.prob}%</span></div>}
                        {contact && (
                          <div style={{ marginTop: 4, padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid " + T.bdr }}>
                            <div style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 4 }}>CONTACT</div>
                            <div style={{ fontSize: 11, color: T.txt, fontWeight: 400 }}>{contact.fn} {contact.ln}</div>
                            {contact.email && <div style={{ fontSize: 10, color: T.tx3 }}>{contact.email}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              };

              const resultBtnStyle = { display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", width: "100%", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", transition: "background 0.12s" };
              const resultBtnHoverBg = "rgba(64,89,173,0.12)";

              const searchRect = searchRef.current?.getBoundingClientRect();
              const dropLeft = searchRect ? (showPreview ? searchRect.left - 120 : searchRect.left) : 0;
              const dropTop = searchRect ? searchRect.bottom + 6 : 58;

              return hasResults ? ReactDOM.createPortal(
                <div ref={searchDropRef} style={{ position: "fixed", top: dropTop, left: dropLeft, display: "flex", background: "#2E3036", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(217,219,221,0.04)", overflow: "hidden", width: showPreview ? 660 : 420, zIndex: 99999 }}>
                  {/* Results list */}
                  <div style={{ width: showPreview ? 340 : "100%", maxHeight: 420, overflowY: "auto", borderRight: showPreview ? "1px solid rgba(217,219,221,0.08)" : "none" }}>
                    {cResults.length > 0 && <div style={{ padding: "10px 14px 6px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".08em" }}>CONTACTS</div>}
                    {cResults.map(c => (
                      <button key={"c"+c.id}
                        onClick={() => { handleSelectContact(c.id); setGlobalSearch(""); setSearchOpen(false); }}
                        onMouseEnter={e => { setSearchHovered("contact" + c.id); e.currentTarget.style.background = resultBtnHoverBg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        style={resultBtnStyle}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: T.blue + "15", border: "1px solid " + T.blue + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 400, color: T.blue, flexShrink: 0 }}>{(c.fn||"?")[0]}{(c.ln||"?")[0]}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.fn} {c.ln}</div>
                          <div style={{ fontSize: 10, color: T.tx3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email || c.company || c.co || ""}</div>
                        </div>
                        {c.score > 0 && <span style={{ fontSize: 9, fontWeight: 400, padding: "2px 6px", borderRadius: 4, background: (c.score >= 80 ? T.grn : c.score >= 50 ? T.amb : T.red) + "18", color: c.score >= 80 ? T.grn : c.score >= 50 ? T.amb : T.red }}>{c.score}</span>}
                      </button>
                    ))}

                    {dResults.length > 0 && <div style={{ padding: "10px 14px 6px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".08em" }}>DEALS</div>}
                    {dResults.map(d => (
                      <button key={"d"+d.id}
                        onClick={() => { handleSelectDeal(d.id); setGlobalSearch(""); setSearchOpen(false); }}
                        onMouseEnter={e => { setSearchHovered("deal" + d.id); e.currentTarget.style.background = resultBtnHoverBg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        style={resultBtnStyle}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: T.grn + "15", border: "1px solid " + T.grn + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 400, color: T.grn, flexShrink: 0 }}>$</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                          <div style={{ fontSize: 10, color: T.tx3 }}>{d.stage}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 400, color: T.grn }}>{fmtDollar(d.val)}</span>
                      </button>
                    ))}

                    {companies.length > 0 && <div style={{ padding: "10px 14px 6px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".08em" }}>COMPANIES</div>}
                    {companies.map(co => (
                      <button key={"co"+co}
                        onClick={() => { setPage("companies"); setGlobalSearch(""); setSearchOpen(false); }}
                        onMouseEnter={e => { setSearchHovered(null); e.currentTarget.style.background = resultBtnHoverBg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        style={resultBtnStyle}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: T.pur + "15", border: "1px solid " + T.pur + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 400, color: T.pur, flexShrink: 0 }}>{co[0]}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{co}</div>
                      </button>
                    ))}

                    {pageResults.length > 0 && <div style={{ padding: "10px 14px 6px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".08em" }}>PAGES & SETTINGS</div>}
                    {pageResults.map(p => (
                      <button key={"pg"+p.route}
                        onClick={() => { setPage(p.route); setGlobalSearch(""); setSearchOpen(false); }}
                        onMouseEnter={e => { setSearchHovered(null); e.currentTarget.style.background = resultBtnHoverBg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        style={resultBtnStyle}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: (p.color || T.tx3) + "12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 400, color: p.color || T.tx3, flexShrink: 0 }}>{p.icon}</div>
                        <div><div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{p.label}</div><div style={{ fontSize: 10, color: T.tx3 }}>{p.desc}</div></div>
                      </button>
                    ))}
                    <div style={{ padding: "8px 14px", fontSize: 9, color: T.tx3, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                      <span style={{ opacity: 0.6 }}>Esc to close</span>
                    </div>
                  </div>

                  {/* Preview panel */}
                  {showPreview && (
                    <div style={{ width: 320, maxHeight: 420, overflowY: "auto", background: "rgba(255,255,255,0.02)" }}>
                      {renderPreview()}
                    </div>
                  )}
                </div>, document.body
              ) : null;
            })()}
          </div>}
          {isMobile && <div style={{ flex: 1 }} />}
          <div style={{ width: isMobile ? "auto" : 250, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
            {!isMobile && <VoiceCommand onNavigate={setPage} onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} />}
            {page !== "dialer" && <PhoneWidget />}
            <CoachBadge />
            <PipelineDoctorBadge onClick={() => setPage("ai-agents")} />
            <NotificationBell onNavigate={(route, actionId, actionType, category, meta) => {
              if (category === 'deal_change' && actionId) { handleSelectDeal(actionId); return; }
              if (category === 'pipeline_doctor' && actionId) { handleSelectDeal(actionId); return; }
              if (actionType === 'contact' && route === 'contacts' && actionId) { handleSelectContact(actionId); return; }
              setPage(route);
            }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: (page === "dashboard" || page === "team-chat" || page === "lists" || page === "calendar" || page === "projects" || page === "contact-record" || page === "deal-record" || page === "contacts" || page === "deals" || page === "call-logs" || page === "sheets") ? "hidden" : "auto", WebkitOverflowScrolling: "touch", padding: (page === "dashboard" || page === "email" || page === "sms" || page === "admin" || page === "ai-agents" || page === "team-chat" || page === "lists" || page === "calendar" || page === "projects" || page === "contact-record" || page === "deal-record" || page === "contacts" || page === "deals" || page === "call-logs" || page === "sheets" || page.startsWith("pipeline_") || page === "linkedin" || page === "outbound") ? 0 : 18 }}>
          <ErrorBoundary key={page}>
          {page === "dashboard" ? (
            <Dashboard setPage={setPage} onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} isMobile={isMobile} />
          ) : (
          <div ref={pageRef} style={{ height: "100%" }}>
          {page === "contacts" && <ContactsList onSelectContact={handleSelectContact} isMobile={isMobile} />}
          {page === "lists" && <ListsPage onSelectContact={handleSelectContact} isMobile={isMobile} />}
          {page === "companies" && <CompaniesPage onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} onSelectCompany={handleSelectCompany} isMobile={isMobile} />}
          {page === "company-record" && selectedCompanyId && <CompanyRecord companyId={selectedCompanyId} onBack={handleBackCompanies} onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} isMobile={isMobile} />}
          {page === "email" && <EmailPage isMobile={isMobile} onSelectContact={handleSelectContact} />}
          {page === "sms" && <SMSPage isMobile={isMobile} />}
          {page === "deals" && <DealsPage onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} isMobile={isMobile} />}
          {page === "deal-record" && selectedDeal && <DealRecord dealId={selectedDeal} onBack={handleBackDeals} onSelectContact={handleSelectContact} isMobile={isMobile} />}
          {page === "contact-record" && selectedContact && <ContactRecord contact={selectedContact} onBack={handleBack} onSelectDeal={handleSelectDeal} onSelectCompany={handleSelectCompany} isMobile={isMobile} />}
          {page === "dialer" && <DialerPage isMobile={isMobile} />}
          {page === "call-logs" && <CallLogsPage isMobile={isMobile} />}
          {page === "tasks" && <TasksPage isMobile={isMobile} />}
          {page === "projects" && <ProjectsPage isMobile={isMobile} />}
          {page === "calendar" && <CalendarPage isMobile={isMobile} />}
          {page === "sheets" && <SheetsPage isMobile={isMobile} />}
          {page === "workflows" && <WorkflowsPage isMobile={isMobile} />}
          {page === "analytics" && <AnalyticsPage isMobile={isMobile} />}
          {page === "team-chat" && <TeamChatPage onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} isMobile={isMobile} />}
          {page === "linkedin" && <LinkedInPage isMobile={isMobile} />}
          {page === "outbound" && <OutboundAgentPage isMobile={isMobile} />}
          {page === "ai-agents" && <AIAgentsPage onSelectContact={handleSelectContact} onSelectDeal={handleSelectDeal} isMobile={isMobile} />}
          {page === "admin" && <AdminCenter initialTab={adminSubTab} onTabChange={(t) => { setAdminSubTab(t); window.history.pushState({}, "", "/admin/" + t.replace(/_/g, "-")); }} isMobile={isMobile} />}
          {page === "setup" && <SetupChecklistPage setPage={setPage} isMobile={isMobile} />}
          {page === "profile" && <UserProfile onClose={() => setPage("dashboard")} isMobile={isMobile} />}
          {page.startsWith("pipeline_") && <CustomPipelinePage pipelineId={parseInt(page.replace("pipeline_", ""))} isMobile={isMobile} />}
          </div>
          )}
          </ErrorBoundary>
        </div>
      </div>
      {page !== "dialer" && <ActiveCallBar />}
    </div>
  );
}

export default function App() {
  // Public booking pages — render completely outside auth
  const bookPath = window.location.pathname;
  if (bookPath.startsWith("/book/")) {
    const parts = bookPath.replace("/book/", "").replace(/\/$/, "").split("/");
    const bSlug = parts[0];
    const bMeeting = parts[1] || null;
    if (bSlug) return <BookingPage slug={bSlug} meetingSlug={bMeeting} />;
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function ReconnectingScreen() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#2C2E33" }}>
      <div style={{ textAlign: "center" }}>
        <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 22, margin: "0 auto 16px", objectFit: "contain", display: "block" }} />
        <div style={{ fontSize: 12, color: "rgba(250,250,250,0.5)", fontWeight: 400 }}>Reconnecting… please wait</div>
      </div>
    </div>
  );
}

function AuthGate() {
  const auth = useAuth();
  const [path, setPath] = useState(window.location.pathname);

  // Listen for pushState navigation between /login and /signup
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (auth.authLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#2C2E33" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 22, margin: "0 auto 16px", objectFit: "contain", display: "block" }} />
          <div style={{ fontSize: 12, color: "rgba(250,250,250,0.5)", fontWeight: 400 }}>Loading Coreflow...</div>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    if (path === "/signup" || path.startsWith("/signup?")) return <SignupPage />;
    return <LoginPage />;
  }

  if (auth.isPasswordRecovery) return <ResetPasswordPage />;

  // Hold the shell while bootstrap retries are in flight. Do NOT fall through
  // to OnboardingPage — transient network errors must never look like "new user".
  if (auth.bootstrapFailed && !auth.currentUser?._orgId) return <ReconnectingScreen />;

  if (auth.needsOnboarding) return <OnboardingPage />;

  // Billing gate. `_org` is only populated when org-billing fetch succeeded.
  // If !org, we don't know yet (transient error or early hydration) — let them
  // through; re-evaluates on the next profile load. Only an explicit "loaded +
  // not exempt + no active sub" blocks.
  const org = auth.currentUser?._org;
  const billingAllowed = !org
    || org.billing_exempt
    || (org.stripe_subscription_id && ["active", "trialing"].includes(org.plan_status));
  if (!billingAllowed) return <BillingRequiredPage />;

  return <StoreProvider><AppInner /></StoreProvider>;
}
